import clsx from 'clsx'
import { ForwardedRef, forwardRef, useEffect, useRef } from 'react'
import { Typography, styled, TextField } from '@mui/material'
import { OTPInput as BaseOTPInput, OTPInputProps } from 'input-otp'
import { toLocale } from '$func/text'

interface Props {
  numFields: number
  onChange: (value: string) => void
  value: string
  className?: string
  hasError?: boolean
  autoComplete?: string
  gap?: number
  maxInputWidth?: number
  autoFocus?: boolean
  disabled?: boolean
  variant?: 'outlined' | 'standard'
}

const GAP = 8
const MAX_INPUT_WIDTH = 80
export const OtpInput = ({
  className,
  numFields,
  onChange,
  value,
  hasError,
  autoComplete,
  autoFocus,
  gap,
  maxInputWidth,
  variant = 'outlined',
  disabled = false,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!autoFocus || disabled) return

    const timeout = setTimeout(() => {
      inputRef.current?.focus()
    }, 500)

    return () => clearTimeout(timeout)
  }, [autoFocus, disabled])

  return (
    <Root
      ref={inputRef}
      maxLength={numFields}
      containerClassName={className}
      onChange={onChange}
      value={toLocale(value)}
      autoComplete={autoComplete}
      disabled={disabled}
      gap={gap}
      maxInputWidth={maxInputWidth}
      render={({ slots }) => (
        <div className="inputs-container">
          {slots.map((slot, idx) => (
            <Typography
              variant="h3"
              className={clsx(
                { ['is-active']: slot.isActive },
                'fake-input',
                variant,
              )}
              key={idx}
            >
              {slot.char || slot.placeholderChar}
              <div
                className={clsx('border-bottom', {
                  ['is-active']: slot.isActive,
                  ['has-error']: hasError,
                })}
              />
              {slot.hasFakeCaret && <div className="fake-caret" />}
            </Typography>
          ))}
        </div>
      )}
    />
  )
}

type RootProps = OTPInputProps & {
  className?: string
  gap?: number
  maxInputWidth?: number
}

const BaseInput = forwardRef(
  ({ className, ...props }: RootProps, ref: ForwardedRef<HTMLInputElement>) => {
    return <BaseOTPInput {...props} ref={ref} containerClassName={className} />
  },
)

const Root = styled(BaseInput)`
  width: 100%;
  overflow: hidden;

  .inputs-container {
    gap: ${({ gap = GAP }) => `${gap}px`};
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
    max-width: ${({ maxLength, maxInputWidth = MAX_INPUT_WIDTH, gap = GAP }) =>
      maxLength * maxInputWidth + (maxLength - 1) * gap}px;
    // @noflip
    direction: ltr;
  }

  div:not(.inputs-container) {
    overflow: hidden;
  }

  .fake-input {
    height: 48px;

    display: flex;
    align-items: flex-end;
    justify-content: center;
    box-sizing: border-box;
    position: relative;
    width: ${({ maxLength, gap = GAP }) =>
      `calc(${100 / maxLength}% - ${((maxLength - 1) * gap) / maxLength}px)`};

    &.standard {
      border-bottom: ${({ theme }) => `1px solid ${theme.palette.neutral300}`};
    }

    &.outlined {
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 10px;
      border: ${({ theme }) => `1px solid ${theme.palette.neutral300}`};
      transition: border-width 0.1s ease;

      &.has-error {
        border: ${({ theme }) => `1px solid ${theme.palette.error300}`};
      }
      &.is-active {
        border: ${({ theme }) => `2px solid ${theme.palette.primary500}`};
      }
    }

    .fake-caret {
      height: 22px;
      width: 1px;
      animation: caret-blink 1.2s ease-out infinite;
      pointer-events: none;
      background-color: ${({ theme }) => theme.palette.primary500};
      position: relative;
      bottom: 4px;
    }

    &.outlined > .fake-caret {
      bottom: 0;
    }

    &.standard > .border-bottom {
      width: 100%;
      height: 2px;
      position: absolute;
      bottom: -1px;
      background-color: ${({ theme }) => theme.palette.primary500};
      transform: scale(0);
      transition: transform 0.3s ease;
      &.has-error {
        background-color: ${({ theme }) => theme.palette.error300};
      }
      &.is-active {
        transform: scale(1);
      }
    }
  }

  @keyframes caret-blink {
    0%,
    70%,
    100% {
      opacity: 1;
    }
    20%,
    50% {
      opacity: 0;
    }
  }
`
